/**
 * Commercial-licence copy: the rights grid and the trust footer.
 *
 * MOCK/FIXTURE DATA. Moved verbatim out of the LyrixaApp monolith. This is legal
 * marketing copy — do not reword it. A backend will eventually serve the rights
 * list alongside the agreement version (see docs/api-contract.md, "Licenses").
 *
 * `icon` is a *name*, not a component, so this file stays free of lucide imports
 * and can be shared with a server payload of the same shape. LICENSE_ICONS below
 * does the mapping, and is the only place that needs touching when a new icon
 * name appears in the data.
 */
import {
  CheckCircle2,
  DollarSign,
  Globe,
  GraduationCap,
  Megaphone,
  Mic,
  Music,
  Upload,
  Users,
  Video,
} from 'lucide-react';
import type { LicenseRight } from '../types/domain';

/** Every right granted by the Commercial License & Creator Toolkits. */
export const LICENSE_RIGHTS: LicenseRight[] = [
  {
    title: 'Sell music commercially',
    desc: 'Sell your AI-generated songs as a commercial product',
    icon: 'DollarSign',
  },
  { title: 'Upload to Spotify', desc: 'Distribute your tracks on Spotify', icon: 'Upload' },
  { title: 'Upload to Apple Music', desc: 'Distribute your tracks on Apple Music', icon: 'Upload' },
  {
    title: 'Upload to Amazon Music',
    desc: 'Distribute your tracks on Amazon Music',
    icon: 'Upload',
  },
  {
    title: 'Upload to YouTube Music',
    desc: 'Distribute your tracks on YouTube Music',
    icon: 'Upload',
  },
  { title: 'Upload to Deezer', desc: 'Distribute your tracks on Deezer', icon: 'Upload' },
  { title: 'Upload to TikTok', desc: 'Use your tracks in TikTok content', icon: 'Video' },
  { title: 'Upload to Instagram', desc: 'Use your tracks in Instagram content', icon: 'Video' },
  {
    title: 'Monetize videos',
    desc: 'Use tracks in monetized YouTube and social videos',
    icon: 'Video',
  },
  { title: 'Client projects', desc: 'Deliver tracks as part of paid client work', icon: 'Users' },
  {
    title: 'Podcasts',
    desc: 'Use tracks as podcast intros, outros, or background music',
    icon: 'Mic',
  },
  { title: 'Courses', desc: 'Use tracks inside paid online courses', icon: 'GraduationCap' },
  {
    title: 'Commercial advertising',
    desc: 'Use tracks in ads and commercial campaigns',
    icon: 'Megaphone',
  },
  { title: 'Sell beats', desc: 'License or sell your generated tracks as beats', icon: 'Music' },
  {
    title: 'Worldwide usage',
    desc: 'Use your license anywhere in the world, no territory limits',
    icon: 'Globe',
  },
];

/** Icon name → component. Unknown names fall back to a tick. */
export const LICENSE_ICONS: Record<string, typeof CheckCircle2> = {
  DollarSign,
  Upload,
  Video,
  Users,
  Mic,
  GraduationCap,
  Megaphone,
  Music,
  Globe,
};

/** When the agreement was last revised, shown above the agreement text. */
export const AGREEMENT_UPDATED_LABEL = 'Last updated Jul 1, 2026 · Version 1.0';
